<template>
  <div v-if="editor" class="editor">
    <v-toolbar class="toolbar" density="compact">
      <v-toolbar-title>
        {{ label }}
        <TooltipIcon
          v-if="required && empty"
          class="ml-1"
          text="Field is required."
        />
      </v-toolbar-title>
      <v-spacer v-if="!readonly" />
      <template v-if="!readonly">
        <v-tooltip text="Bold (Ctrl-B)" location="bottom">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-format-bold"
              density="compact"
              class="action"
              @click="editor.chain().focus().toggleBold().run()"
              :active="editor.isActive('bold')"
            >
            </v-btn>
          </template>
        </v-tooltip>
        <v-tooltip text="Italic (Ctrl-I)" location="bottom">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-format-italic"
              density="compact"
              class="action"
              @click="editor.chain().focus().toggleItalic().run()"
              :active="editor.isActive('italic')"
            >
            </v-btn> </template
        ></v-tooltip>

        <v-tooltip text="Header 4" location="bottom">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-format-header-4"
              density="compact"
              class="action"
              @click="editor.chain().focus().toggleHeading({ level: 4 }).run()"
              :active="editor.isActive('heading', { level: 4 })"
            >
            </v-btn> </template
        ></v-tooltip>
        <v-tooltip text="Header 5" location="bottom">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-format-header-5"
              density="compact"
              class="action"
              @click="editor.chain().focus().toggleHeading({ level: 5 }).run()"
              :active="editor.isActive('heading', { level: 5 })"
            >
            </v-btn> </template
        ></v-tooltip>
        <v-tooltip text="Header 6" location="bottom">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-format-header-6"
              density="compact"
              class="action"
              @click="editor.chain().focus().toggleHeading({ level: 6 }).run()"
              :active="editor.isActive('heading', { level: 6 })"
            >
            </v-btn> </template
        ></v-tooltip>

        <v-tooltip
          v-if="!editor.isActive('bulletList')"
          text="Toggle Bullet List"
          location="bottom"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-format-list-bulleted"
              class="action"
              density="compact"
              @click="editor.chain().focus().toggleBulletList().run()"
            >
            </v-btn> </template
        ></v-tooltip>
        <span v-else class="temporary-actions">
          <v-tooltip text="Sink List Item" location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-format-indent-increase"
                class="action"
                density="compact"
                @click="editor.chain().focus().sinkListItem('listItem').run()"
              >
              </v-btn> </template
          ></v-tooltip>
          <v-tooltip text="Lift List Item" location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-format-indent-decrease"
                class="action"
                density="compact"
                @click="editor.chain().focus().liftListItem('listItem').run()"
              >
              </v-btn> </template
          ></v-tooltip>
        </span>

        <v-tooltip
          v-if="!editor.isActive('orderedList')"
          text="Toggle Ordered List"
          location="bottom"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-format-list-numbered"
              class="action"
              density="compact"
              @click="editor.chain().focus().toggleOrderedList().run()"
            >
            </v-btn> </template
        ></v-tooltip>
        <span v-else class="temporary-actions">
          <v-tooltip text="Sink List Item" location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-format-indent-increase"
                class="action"
                density="compact"
                @click="editor.chain().focus().sinkListItem('listItem').run()"
              >
              </v-btn> </template
          ></v-tooltip>
          <v-tooltip text="Lift List Item" location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-format-indent-decrease"
                class="action"
                density="compact"
                @click="editor.chain().focus().liftListItem('listItem').run()"
              >
              </v-btn> </template
          ></v-tooltip>
        </span>

        <v-tooltip
          v-if="!editor.isActive('table')"
          text="Insert Table"
          location="bottom"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-table"
              density="compact"
              class="action"
              @click="
                editor
                  .chain()
                  .focus()
                  .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                  .run()
              "
            >
            </v-btn> </template
        ></v-tooltip>
        <span v-else class="temporary-actions">
          <v-tooltip text="Insert Column Before" location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-table-column-plus-before"
                density="compact"
                class="action"
                @click="editor.chain().focus().addColumnBefore().run()"
              >
              </v-btn> </template
          ></v-tooltip>
          <v-tooltip text="Insert Column After" location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-table-column-plus-after"
                density="compact"
                class="action"
                @click="editor.chain().focus().addColumnAfter().run()"
              >
              </v-btn> </template
          ></v-tooltip>
          <v-tooltip text="Remove Column" location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-table-column-remove"
                density="compact"
                class="action"
                @click="editor.chain().focus().deleteColumn().run()"
              >
              </v-btn> </template
          ></v-tooltip>
          <v-tooltip text="Insert Row Before" location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-table-row-plus-before"
                density="compact"
                class="action"
                @click="editor.chain().focus().addRowBefore().run()"
              >
              </v-btn> </template
          ></v-tooltip>
          <v-tooltip text="Insert Row After" location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-table-row-plus-after"
                density="compact"
                class="action"
                @click="editor.chain().focus().addRowAfter().run()"
              >
              </v-btn> </template
          ></v-tooltip>
          <v-tooltip text="Remove Row" location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-table-row-remove"
                density="compact"
                class="action"
                @click="editor.chain().focus().deleteRow().run()"
              >
              </v-btn> </template
          ></v-tooltip>

          <v-tooltip text="Merge Cells" location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-table-merge-cells"
                density="compact"
                class="action"
                @click="editor.chain().focus().mergeCells().run()"
              >
              </v-btn> </template
          ></v-tooltip>
          <v-tooltip text="Split Cell" location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-table-split-cell"
                density="compact"
                class="action"
                @click="editor.chain().focus().splitCell().run()"
              >
              </v-btn> </template
          ></v-tooltip>
        </span>
      </template>
    </v-toolbar>
    <div class="field diff-content">
      <editor-content :editor="editor" v-model="model" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Editor, EditorContent } from "@tiptap/vue-3";
import {
  onBeforeUnmount,
  onMounted,
  ref,
  defineModel,
  watch,
  computed,
} from "vue";
import sanitizeHtml from "sanitize-html";
import { RichTextExtension, type RichTextExtensionOptions } from "./rich-text";
import TooltipIcon from "../tooltips/TooltipIcon.vue";
import { emptyRichText } from "@/plugins/html";
import diff from "html-diff-ts";

const editor = ref<Editor>();

const model = defineModel<string>();

const props = withDefaults(
  defineProps<{
    label: string;
    diffValue?: string;
    options?: Partial<RichTextExtensionOptions>;
    required?: boolean;
    readonly?: boolean;
  }>(),
  {
    options: () => {
      return {
        table: {
          resizable: true,
        },
      } as Partial<RichTextExtensionOptions>;
    },
    required: false,
    readonly: false,
  },
);

const empty = computed(() => {
  return emptyRichText(model.value);
});

watch(
  () => [model.value, props.diffValue],
  () => {
    const html = sanitizeHtml(model.value);
    if (props.options.diff) {
      const diffHtml = sanitizeHtml(props.diffValue ?? "");
      const diffResult = diff(diffHtml, html);
      editor.value.commands.setContent(diffResult, false);
    } else if (sanitizeHtml(editor.value.getHTML()) !== html) {
      editor.value.commands.setContent(html, false);
    }
  },
);

onMounted(() => {
  let html = sanitizeHtml(model.value);
  if (props.options.diff) {
    const diffHtml = sanitizeHtml(props.diffValue ?? "");
    html = diff(diffHtml, html);
  }
  editor.value = new Editor({
    content: html,
    editable: !props.readonly,
    extensions: [RichTextExtension.configure(props.options)],
    onUpdate: () => {
      model.value = sanitizeHtml(editor.value.getHTML());
    },
  });
});

onBeforeUnmount(() => {
  editor.value.destroy();
});
</script>

<style lang="scss" scoped>
.editor {
  border: 1px solid black;
  border-radius: 8px;

  .toolbar {
    border-radius: 8px 8px 0px 0px;

    .action {
      margin-left: 4px;
      margin-right: 4px;
    }
  }

  .field {
    padding: 8px;
  }
}
</style>

<style>
.ProseMirror:focus {
  outline: none;
}

/* deletion in red with strike-through */
.diff-content del {
  color: red;
}

/* insertion in green */
.diff-content ins {
  color: green;
  background-color: rgb(230, 255, 230);
}
</style>

<style lang="scss">
@use "./rich-text.scss";
</style>
